import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    Typography,
    IconButton,
    Tooltip,
    ClickAwayListener,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { NumericFormat } from 'react-number-format';
import { apiService } from '../../../../services/api';
import { formatNumber } from '../../../../utils/formatters';
import { colors } from '../../../../styles/colors';
import { Ertragsprognose, DeductionItem, DeductionItemUpdate } from '../../../../types';


// Editierbare Bezeichnung - wird bei Klick zum Textfeld

interface EditableLabelProps {
    value: string;
    canEdit: boolean;
    onChange: (value: string) => void;
    compact?: boolean;
}

const EditableLabel: React.FC<EditableLabelProps> = ({
    value,
    canEdit,
    onChange,
    compact = false,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleClick = () => {
        if (canEdit) {
            setEditValue(value);
            setIsEditing(true);
        }
    };

    const handleSave = () => {
        if (editValue.trim() && editValue !== value) {
            onChange(editValue.trim());
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    if (isEditing) {
        return (
            <ClickAwayListener onClickAway={handleSave}>
                <TextField
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    size="small"
                    variant="standard"
                    sx={{
                        flex: 1,
                        minWidth: compact ? 120 : 150,
                        '& .MuiInput-input': {
                            fontSize: compact ? '0.875rem' : '1rem',
                            py: 0.5,
                        }
                    }}
                />
            </ClickAwayListener>
        );
    }

    return (
        <Typography
            onClick={handleClick}
            variant={compact ? 'body2' : 'body1'}
            sx={{
                flex: 1,
                minWidth: compact ? 120 : 150,
                cursor: canEdit ? 'pointer' : 'default',
                '&:hover': canEdit ? {
                    backgroundColor: colors.opacity.black04,
                    borderRadius: 1,
                } : {},
                px: 1,  // Einheitliches Padding für alle Labels
                py: 0.5,
                transition: 'background-color 0.2s',
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </Typography>
    );
};

// Einzelne Abzugs-Zeile

interface DeductionRowProps {
    item: DeductionItem;
    canEdit: boolean;
    isSaving: boolean;
    onUpdate: (itemId: string, data: DeductionItemUpdate) => void;
    onDelete: (itemId: string) => void;
}

const DeductionRow: React.FC<DeductionRowProps> = ({
    item,
    canEdit,
    isSaving,
    onUpdate,
    onDelete,
}) => {
    const [localValue, setLocalValue] = useState(item.wert);

    // Aktualisiere lokalen Wert wenn sich das Item ändert
    React.useEffect(() => {
        setLocalValue(item.wert);
    }, [item.wert]);

    const handleValueChange = (value: number) => {
        setLocalValue(value);
    };

    const handleValueBlur = () => {
        if (localValue !== item.wert) {
            onUpdate(item.id, { wert: localValue });
        }
    };

    const handleLabelChange = (value: string) => {
        onUpdate(item.id, { bezeichnung: value });
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.25,
        }}>
            {/* Löschen-Button - ganz links */}
            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
                {canEdit ? (
                    <Tooltip title="Position entfernen">
                        <IconButton
                            size="small"
                            onClick={() => onDelete(item.id)}
                            disabled={isSaving}
                            sx={{
                                color: colors.icon.subtle,
                                '&:hover': { color: 'error.main' },
                                p: 0.5,
                            }}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Box sx={{ width: 28 }} />
                )}
            </Box>
            {/* Editierbare Bezeichnung */}
            <EditableLabel
                value={item.bezeichnung}
                canEdit={canEdit && !isSaving}
                onChange={handleLabelChange}
                compact
            />
            {/* Wert-Eingabe */}
            <Box sx={{ width: 100 }}>
                {canEdit ? (
                    <NumericFormat
                        customInput={TextField}
                        value={localValue}
                        onValueChange={(values) => {
                            const val = values.floatValue ?? 0;
                            handleValueChange(Math.min(Math.max(val, 0), 100));
                        }}
                        onBlur={handleValueBlur}
                        decimalSeparator=","
                        thousandSeparator="."
                        decimalScale={2}
                        allowNegative={false}
                        isAllowed={(values) => {
                            const { floatValue } = values;
                            return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
                        }}
                        size="small"
                        disabled={isSaving}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-input': {
                                py: 0.75,
                                px: 1,
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                ) : (
                    <TextField
                        value={`${formatNumber(item.wert, 2)}`}
                        size="small"
                        slotProps={{
                            input: {
                                readOnly: true,
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-input': {
                                py: 0.75,
                                px: 1,
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

// Hauptkomponente

interface YieldForecastDeductionsContentProps {
    /** Die Ertragsprognose, deren Abzüge angezeigt werden */
    forecast: Ertragsprognose;
    /** Ob Bearbeitung erlaubt ist */
    canEdit: boolean;
    /** Callback wenn Daten geändert wurden */
    onDataChanged?: () => void;
}

/**
 * Content für die expandierte Zeile einer Ertragsprognose
 * Zeigt die einzelnen Abzüge editierbar an
 * 
 * Features:
 * - Dynamische Abzugspositionen aus deduction_items
 * - Editierbare Bezeichnungen (außer für geschützte Items)
 * - Hinzufügen und Entfernen von Positionen
 * - Gesamtabzug wird multiplikativ berechnet
 */
const YieldForecastDeductionsContent: React.FC<YieldForecastDeductionsContentProps> = ({
    forecast,
    canEdit,
    onDataChanged,
}) => {
    const [isSaving, setIsSaving] = useState(false);

    // Sortierte Items
    const sortedItems = useMemo(() => {
        if (!forecast.deduction_items) return [];
        return [...forecast.deduction_items].sort((a, b) => a.sort_order - b.sort_order);
    }, [forecast.deduction_items]);

    const handleUpdateItem = useCallback(async (itemId: string, data: DeductionItemUpdate) => {
        if (!canEdit || isSaving) return;

        setIsSaving(true);
        try {
            await apiService.updateDeductionItem(forecast.project_id, forecast.id, itemId, data);
            onDataChanged?.();
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    }, [canEdit, isSaving, forecast.id, forecast.project_id, onDataChanged]);

    const handleDeleteItem = useCallback(async (itemId: string) => {
        if (!canEdit || isSaving) return;

        setIsSaving(true);
        try {
            await apiService.deleteDeductionItem(forecast.project_id, forecast.id, itemId);
            onDataChanged?.();
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    }, [canEdit, isSaving, forecast.id, forecast.project_id, onDataChanged]);

    const handleAddItem = useCallback(async () => {
        if (!canEdit || isSaving) return;

        setIsSaving(true);
        try {
            await apiService.createDeductionItem(forecast.project_id, forecast.id, {
                bezeichnung: 'Neue Position',
                wert: 0,
            });
            onDataChanged?.();
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    }, [canEdit, isSaving, forecast.id, forecast.project_id, onDataChanged]);

    // Fallback für alte Daten ohne deduction_items
    if (!forecast.deduction_items || forecast.deduction_items.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{
                    color: "text.secondary"
                }}>
                    Keine Abzugspositionen vorhanden. Bitte Migration ausführen.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Abzugs-Positionen in 2-Spalten Grid */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                rowGap: 0.5,
                columnGap: 6,
            }}>
                {sortedItems.map((item) => (
                    <DeductionRow
                        key={item.id}
                        item={item}
                        canEdit={canEdit}
                        isSaving={isSaving}
                        onUpdate={handleUpdateItem}
                        onDelete={handleDeleteItem}
                    />
                ))}
            </Box>

            {/* Hinzufügen-Button */}
            {canEdit && (
                <Box sx={{ mt: 0.5, display: 'flex' }}>
                    <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Position anlegen">
                            <IconButton
                                size="small"
                                onClick={handleAddItem}
                                disabled={isSaving}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                                    p: 0.5,
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default YieldForecastDeductionsContent;
